import BaseSelect from 'components/Selects/BaseSelect/BaseSelect';
import useSirenAudio from 'features/Integrations/hooks/useSirenAudio';
import { useMemo } from 'react';
import { audioLabel, sirenAudioOptionsFor } from '../../utils/settings.utils';
import { isSirenDevice } from '../../../../utils/settings.utils';

const SIREN_SELECT_STYLES = {
  container: (base) => ({ ...base, maxWidth: 360 }),
};

/** The "same as group" entry; a null audio means "no per-device override". */
const sameAsGroupOption = (groupAudio, groupLabel) => ({
  value: '',
  label: `Same as group${groupAudio ? ` — ${groupLabel}` : ''}`,
  audio: null,
});

const toAudioOption = (audio) => ({ value: String(audio.id), label: audioLabel(audio), audio });

/**
 * Builds react-select grouped options mirroring the native `<optgroup>` layout.
 */
const buildAudioGroups = ({ universal, own, elsewhere, groupAudio, groupLabel }) => [
  sameAsGroupOption(groupAudio, groupLabel),
  ...(universal.length > 0
    ? [{ label: 'On every siren', options: universal.map(toAudioOption) }]
    : []),
  ...(own.length > 0 ? [{ label: 'Only on this siren', options: own.map(toAudioOption) }] : []),
  ...(elsewhere.length > 0
    ? [
        {
          label: 'Elsewhere — will be added to this siren on Save',
          options: elsewhere.map(toAudioOption),
        },
      ]
    : []),
];

/**
 * Finds the option for the currently selected audio id across all three groups.
 */
const findAudioOption = ({ universal, own, elsewhere, groupAudio, groupLabel }, currentKey) => {
  if (currentKey === '') return sameAsGroupOption(groupAudio, groupLabel);
  const audio = [...universal, ...own, ...elsewhere].find((a) => String(a.id) === currentKey);
  return audio ? toAudioOption(audio) : null;
};

/**
 * GroupSirenAudioRows — one row per siren in the group: which file THIS siren
 * plays when the group is triggered.
 *
 * Mike, 2026-08-28: "trigger a group of sirens together with the same audio
 * file for each, or a separate one if we have the options available per
 * device in some clever way."
 *
 * The group controls above pick the file for the whole group — from the files
 * that exist on every siren. A row here can override it for one siren, and
 * its picker lists only what that siren can actually play: the universal
 * files, and the files uploaded to that siren alone
 * (`RI_Device_Audio_Files.device_ids`). The backend checks the same rule and
 * refuses the whole send if a row asks for a file the siren does not have, so
 * nothing half-fires.
 *
 * No "simulated" badge on a row: the sirens are named for what they are
 * (Mike, 2026-08-28: "we will know by naming them appropriately").
 *
 * `value` is `{ [deviceId]: audio | null }` — null or absent means "same as
 * the group".
 */
const GroupSirenAudioRows = ({ devices, groupAudio, value, onChange, disabled = false }) => {
  const { allAudio } = useSirenAudio(devices);
  const sirens = useMemo(() => (devices || []).filter(isSirenDevice), [devices]);
  if (sirens.length === 0) return null;

  const groupLabel = groupAudio ? audioLabel(groupAudio) : 'no file chosen yet';
  const overridden = sirens.filter((d) => value?.[d.id]).length;
  const onGroupFile = sirens.length - overridden;
  const sirenWord = (count) => `siren${count === 1 ? '' : 's'}`;
  // "all 11 sirens" while every row follows the group; once a row picks its own
  // file the count says how many still play the group's.
  const playsOn =
    overridden > 0
      ? `Plays on ${onGroupFile} of ${sirens.length} ${sirenWord(sirens.length)}`
      : `Plays on all ${sirens.length} ${sirenWord(sirens.length)}`;

  return (
    <div className="bg-primary rounded-3 p-3 d-flex flex-column gap-2">
      <div className="d-flex flex-wrap align-items-baseline gap-2">
        <span className="fw-semibold">Selected Audio File:</span>
        <span className="text-secondary small">
          <span className="fw-semibold">{groupLabel}</span>
          {` · ${playsOn}`}
        </span>
      </div>
      <div className="text-secondary small">
        A device set to &ldquo;Same as group&rdquo; plays the audio file selected for the group. If
        the selected file does not already exist on a device, it will be uploaded when you click
        Save. The file will then play immediately when the included siren and giant voice devices
        enter the Alerting status. Devices without a camera or audio storage capability will be
        skipped.
      </div>
      <div className="d-flex flex-column gap-1">
        {sirens.map((device) => {
          const { universal, own, elsewhere } = sirenAudioOptionsFor(device, allAudio);
          const current = value?.[device.id] ?? null;
          const currentKey = current ? String(current.id) : '';
          return (
            <div
              key={device.id}
              className="d-flex flex-column flex-lg-row align-items-lg-center gap-2"
            >
              <div
                className="flex-grow-1 text-truncate"
                title={`${device.alias || device.name || device.id} (${device.node_id})`}
              >
                {device.alias || device.name || `device ${device.id}`}
              </div>
              <BaseSelect
                wrapperClassName="flex-grow-1"
                isSearchable
                styles={SIREN_SELECT_STYLES}
                selectProps={{
                  'aria-label': `Audio for ${device.alias || device.name || `device ${device.id}`}`,
                  options: buildAudioGroups({ universal, own, elsewhere, groupAudio, groupLabel }),
                  value: findAudioOption(
                    { universal, own, elsewhere, groupAudio, groupLabel },
                    currentKey,
                  ),
                  isDisabled: disabled,
                  onChange: (option) => onChange(device.id, option?.audio ?? null),
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GroupSirenAudioRows;
